import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Δευτέρα', shortLabel: 'ΔΕΥ' },
  { value: 2, label: 'Τρίτη', shortLabel: 'ΤΡΙ' },
  { value: 3, label: 'Τετάρτη', shortLabel: 'ΤΕΤ' },
  { value: 4, label: 'Πέμπτη', shortLabel: 'ΠΕΜ' },
  { value: 5, label: 'Παρασκευή', shortLabel: 'ΠΑΡ' },
  { value: 6, label: 'Σάββατο', shortLabel: 'ΣΑΒ' },
  { value: 0, label: 'Κυριακή', shortLabel: 'ΚΥΡ' },
];

const POSITIONS = [
  { value: 'service', label: 'Service' },
  { value: 'buffet', label: 'Μπουφές' },
  { value: 'kitchen', label: 'Κουζίνα' },
  { value: 'bar', label: 'Μπαρ' },
  { value: 'cashier', label: 'Ταμείο' },
];

const POSITION_COLORS = {
  service: '#3b82f6',
  buffet: '#f59e0b',
  kitchen: '#ef4444',
  bar: '#8b5cf6',
  cashier: '#06b6d4',
};

const calculateShiftDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 8;
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  const startMinutes = startHour * 60 + startMin;
  let endMinutes = endHour * 60 + endMin;
  if (endMinutes <= startMinutes) endMinutes += 24 * 60;
  return Math.round((endMinutes - startMinutes) / 60 * 10) / 10;
};

const WeeklyStaffingRequirements = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [currentView, setCurrentView] = useState('weekly');
  const [selectedDay, setSelectedDay] = useState(null);
  const [staffingData, setStaffingData] = useState({});
  const [openForm, setOpenForm] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    time_start: '09:00',
    time_end: '17:00',
    position: 'service',
    required_staff: 1,
    shift_duration_hours: 8,
  });

  useEffect(() => {
    const d = calculateShiftDuration(formData.time_start, formData.time_end);
    if (d !== formData.shift_duration_hours) {
      setFormData((prev) => ({ ...prev, shift_duration_hours: d }));
    }
  }, [formData.time_start, formData.time_end]);

  useEffect(() => {
    fetchStaffingRequirements();
  }, []);

  const fetchStaffingRequirements = async () => {
    try {
      const response = await fetch('/api/store-settings/staffing-requirements');
      if (response.ok) {
        const data = await response.json();
        const grouped = data.reduce((acc, item) => {
          if (!acc[item.day_of_week]) acc[item.day_of_week] = [];
          acc[item.day_of_week].push(item);
          return acc;
        }, {});
        setStaffingData(grouped);
      } else {
        setError('Σφάλμα κατά τη φόρτωση των δεδομένων');
      }
    } catch {
      setError('Σφάλμα κατά τη φόρτωση των δεδομένων');
    }
  };

  const getShiftCount = (dayOfWeek) => staffingData[dayOfWeek]?.length || 0;
  const getPositionLabel = (position) => POSITIONS.find((p) => p.value === position)?.label || position;
  const getDayLabel = (dayOfWeek) => DAYS_OF_WEEK.find((d) => d.value === dayOfWeek)?.label || '';
  const getTimePeriod = (timeStart) => {
    const hour = parseInt(timeStart.split(':')[0]);
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  };

  const getTotalStaff = (dayOfWeek) => {
    return (staffingData[dayOfWeek] || []).reduce((sum, s) => sum + s.required_staff, 0);
  };

  const handleDayClick = (dayOfWeek) => {
    setSelectedDay(dayOfWeek);
    setCurrentView('day');
    setError('');
    setSuccess('');
  };

  const handleAddShift = () => {
    setEditingShift(null);
    setFormData({ time_start: '09:00', time_end: '17:00', position: 'service', required_staff: 1, shift_duration_hours: 8 });
    setOpenForm(true);
  };

  const handleEditShift = (shift) => {
    setEditingShift(shift);
    setFormData({
      time_start: shift.time_start,
      time_end: shift.time_end,
      position: shift.position,
      required_staff: shift.required_staff,
      shift_duration_hours: shift.shift_duration_hours || 8,
    });
    setOpenForm(true);
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή τη βάρδια;')) return;
    try {
      setLoading(true);
      const response = await fetch(`/api/store-settings/staffing-requirements/${shiftId}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Η βάρδια διαγράφηκε επιτυχώς');
        fetchStaffingRequirements();
      } else {
        setError('Σφάλμα κατά τη διαγραφή');
      }
    } catch {
      setError('Σφάλμα κατά τη διαγραφή');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      const submitData = {
        day_of_week: selectedDay,
        time_period: getTimePeriod(formData.time_start),
        time_start: formData.time_start,
        time_end: formData.time_end,
        position: formData.position,
        required_staff: formData.required_staff,
        shift_duration_hours: formData.shift_duration_hours,
      };

      const url = editingShift
        ? `/api/store-settings/staffing-requirements/${editingShift.id}`
        : '/api/store-settings/staffing-requirements';
      const method = editingShift ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setSuccess(editingShift ? 'Η βάρδια ενημερώθηκε επιτυχώς' : 'Η βάρδια προστέθηκε επιτυχώς');
        setOpenForm(false);
        await fetchStaffingRequirements();
      } else {
        setError('Σφάλμα κατά την αποθήκευση');
      }
    } catch {
      setError('Σφάλμα κατά την αποθήκευση');
    } finally {
      setLoading(false);
    }
  };

  const renderFormDialog = () => (
    <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {editingShift ? 'Επεξεργασία Βάρδιας' : 'Νέα Βάρδια'} - {getDayLabel(selectedDay)}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Ώρα Έναρξης"
              type="time"
              value={formData.time_start}
              onChange={(e) => setFormData({ ...formData, time_start: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Ώρα Λήξης"
              type="time"
              value={formData.time_end}
              onChange={(e) => setFormData({ ...formData, time_end: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Θέση</InputLabel>
              <Select
                value={formData.position}
                label="Θέση"
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                sx={{ borderRadius: 2 }}
              >
                {POSITIONS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Άτομα"
              type="number"
              value={formData.required_staff}
              onChange={(e) => setFormData({ ...formData, required_staff: parseInt(e.target.value) || 1 })}
              fullWidth
              inputProps={{ min: 1 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
          <Box sx={{
            p: 2, borderRadius: 2,
            bgcolor: 'rgba(0,128,96,0.04)',
            border: '1px solid rgba(0,128,96,0.1)',
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <ScheduleIcon sx={{ color: '#008060', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              Διάρκεια: <strong>{formData.shift_duration_hours} ώρες</strong> (υπολογίζεται αυτόματα)
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setOpenForm(false)} disabled={loading} sx={{ borderRadius: 2 }}>
          Ακύρωση
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ borderRadius: 2, fontWeight: 600, px: 3, boxShadow: '0 4px 14px rgba(0,128,96,0.3)' }}
        >
          {loading ? 'Αποθήκευση...' : editingShift ? 'Ενημέρωση' : 'Προσθήκη'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Weekly View
  if (currentView === 'weekly') {
    return (
      <>
        <Paper sx={{
          borderRadius: 3, overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.06)',
        }}>
          <Box sx={{
            p: { xs: 2, md: 3 },
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            bgcolor: 'rgba(0,0,0,0.01)',
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CalendarIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Εβδομαδιαία Προβολή
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

            <Grid container spacing={2}>
              {DAYS_OF_WEEK.map((day) => {
                const shiftCount = getShiftCount(day.value);
                const totalStaff = getTotalStaff(day.value);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={day.value}>
                    <Card
                      sx={{
                        cursor: 'pointer', borderRadius: 2.5,
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0,128,96,0.12)',
                          borderColor: 'rgba(0,128,96,0.3)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                      onClick={() => handleDayClick(day.value)}
                    >
                      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                            {day.label}
                          </Typography>
                          <CalendarIcon sx={{ color: shiftCount > 0 ? '#008060' : '#ccc', fontSize: 20 }} />
                        </Box>

                        {shiftCount === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Καμία βάρδια
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              icon={<ScheduleIcon sx={{ fontSize: '14px !important' }} />}
                              label={`${shiftCount} ${shiftCount === 1 ? 'βάρδια' : 'βάρδιες'}`}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(0,128,96,0.08)', color: '#008060',
                                fontWeight: 600, fontSize: '0.7rem',
                                '& .MuiChip-icon': { color: '#008060' },
                              }}
                            />
                            <Chip
                              icon={<PeopleIcon sx={{ fontSize: '14px !important' }} />}
                              label={`${totalStaff} άτομα`}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(59,130,246,0.08)', color: '#1d4ed8',
                                fontWeight: 600, fontSize: '0.7rem',
                                '& .MuiChip-icon': { color: '#1d4ed8' },
                              }}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Paper>
        {renderFormDialog()}
      </>
    );
  }

  // Day Detail View
  if (currentView === 'day') {
    const dayShifts = staffingData[selectedDay] || [];

    return (
      <>
        <Paper sx={{
          borderRadius: 3, overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.06)',
        }}>
          {/* Day header */}
          <Box sx={{
            p: { xs: 2, md: 3 },
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            bgcolor: 'rgba(0,0,0,0.01)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title="Πίσω στην εβδομάδα">
                <IconButton
                  onClick={() => { setCurrentView('weekly'); setError(''); setSuccess(''); }}
                  sx={{
                    border: '1px solid rgba(0,0,0,0.1)', borderRadius: 2,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  }}
                  size="small"
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                  {getDayLabel(selectedDay)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayShifts.length} {dayShifts.length === 1 ? 'βάρδια' : 'βάρδιες'}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddShift}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderRadius: 2, textTransform: 'none', fontWeight: 600,
                boxShadow: '0 4px 14px rgba(0,128,96,0.3)',
              }}
            >
              Βάρδια
            </Button>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

            {dayShifts.length === 0 ? (
              <Box sx={{
                textAlign: 'center', py: 6,
                bgcolor: 'rgba(0,0,0,0.01)', borderRadius: 2,
                border: '2px dashed rgba(0,0,0,0.1)',
              }}>
                <ScheduleIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Δεν υπάρχουν βάρδιες για αυτή την ημέρα
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddShift}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Προσθήκη Πρώτης Βάρδιας
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {dayShifts.map((shift) => {
                  const posColor = POSITION_COLORS[shift.position] || '#6b7280';
                  const duration = calculateShiftDuration(shift.time_start, shift.time_end);
                  return (
                    <Box key={shift.id} sx={{
                      p: 2, borderRadius: 2,
                      border: '1px solid rgba(0,0,0,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.01)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Box sx={{
                          width: 4, height: 48, borderRadius: 2,
                          bgcolor: posColor,
                        }} />
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                              {shift.time_start} - {shift.time_end}
                            </Typography>
                            <Chip
                              label={`${duration}ωρο`}
                              size="small"
                              sx={{
                                height: 22, fontSize: '0.7rem', fontWeight: 600,
                                bgcolor: 'rgba(0,128,96,0.08)', color: '#008060',
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Chip
                              icon={<WorkIcon sx={{ fontSize: '12px !important' }} />}
                              label={getPositionLabel(shift.position)}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 24, fontSize: '0.7rem', fontWeight: 500,
                                borderColor: `${posColor}40`, color: posColor,
                                '& .MuiChip-icon': { color: posColor },
                              }}
                            />
                            <Chip
                              icon={<PeopleIcon sx={{ fontSize: '12px !important' }} />}
                              label={`${shift.required_staff} άτομα`}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 24, fontSize: '0.7rem', fontWeight: 500,
                                borderColor: 'rgba(0,0,0,0.15)', color: '#666',
                                '& .MuiChip-icon': { color: '#999' },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Επεξεργασία">
                          <IconButton
                            size="small"
                            onClick={() => handleEditShift(shift)}
                            sx={{ '&:hover': { color: '#008060', bgcolor: 'rgba(0,128,96,0.08)' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Διαγραφή">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteShift(shift.id)}
                            sx={{ '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Paper>
        {renderFormDialog()}
      </>
    );
  }

  return null;
};

export default WeeklyStaffingRequirements;
